import { Radio, type RadioChangeEvent } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { CenterFlex, Flex, Space, Sticky, VFlex } from '@/components/base';
import type { SentimentChartData } from '@/components/Chart/SentimentTrendChart/SentimentTrendChart';
import { SentimentTrendChart } from '@/components/Chart/SentimentTrendChart/SentimentTrendChart';
import { GuideInfo } from '@/components/Chart/SentimentTrendChart/SentimentTrendChartTitle';
import ScrollView from '@/components/Common/ScrollView';
import Typo from '@/components/Typography/Typo';
import SentimentFeedbackItem from '@/features/Feedback/field/Sentiment/SentimentFeedbackItem';
import SentimentInfo from '@/features/Feedback/field/Sentiment/SentimentInfo';
import useColor from '@/hooks/domain/useColor';
import useIntersectionObserver from '@/hooks/pure/useIntersectionObserver';
import useFeedbackNavigationStore from '@/stores/Feedback/FeedbackNavigationStore';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { scrollToTargetAdjusted } from '@/utils/dom';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

type Props = {
  alias: string;
  data: LinkedFeedbackResponse;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

type SentimentCategoryT = 'all' | 'negative' | 'positive';

const PAGE_SIZE = 50;

const SentimentTab = (props: Props) => {
  const scrollElementId = `content_detail_modal_scroll_selector_${props.alias}`;
  const color = useColor();
  const loaderRef = useRef(null);
  const [page, setPage] = useState(1);
  const [sentimentCategory, setSentimentCategory] =
    useState<SentimentCategoryT>('all');
  const onChange = (e: RadioChangeEvent) => {
    const scrollTarget = document.querySelector(`#${scrollElementId}`);
    if (!scrollTarget) return;
    scrollTarget.scrollTo(0, 0);
    setSentimentCategory(e.target.value);
    setPage(1);
  };

  const isFeedbackInCategory = useCallback(
    (feedback: FeedbackMinimalListItemT, category: SentimentCategoryT) => {
      switch (category) {
        case 'all':
          return true;
        case 'negative':
          return feedback.sentiment === 3;
        case 'positive':
          return feedback.sentiment === 1;
        default:
          return false;
      }
    },
    [],
  );

  /**
   * Returns an array of the 50 most recent feedbacks from the provided data.
   * The feedbacks are sorted in ascending order based on their date.
   */
  const recentFeedbacks = useMemo(() => {
    return props.data?.feedbacks
      .toSorted((a, b) => {
        return getTimezoneDayjs(a.date).diff(getTimezoneDayjs(b.date));
      })
      .slice(-50);
  }, [props.data]);

  const parsedChartData: SentimentChartData[] = useMemo(() => {
    return recentFeedbacks.map((feedback) => {
      return {
        alias: feedback.alias,
        date: feedback.date,
        sentiment: feedback.sentiment,
        hide: !isFeedbackInCategory(feedback, sentimentCategory),
      };
    });
  }, [recentFeedbacks, isFeedbackInCategory, sentimentCategory]);

  const chartLabels = useMemo(() => {
    return recentFeedbacks.map((feedback, index) => {
      if (index === 0 || index === recentFeedbacks.length - 1) {
        return getTimezoneDayjs(feedback.date).format(DATE_FORMAT.readable);
      }
    });
  }, [recentFeedbacks]);

  const [selectedFeedback, setSelectedFeedback] = useState<
    number | string | null
  >(null);

  const { setToPrevFeedback, setToNextFeedback } = useFeedbackNavigationStore(
    (state) => ({
      setToPrevFeedback: state.setToPrevFeedback,
      setToNextFeedback: state.setToNextFeedback,
    }),
  );

  const delay = 200;

  const toPrevFeedback = () => {
    const currIndex = feedbackList?.current.findIndex(
      (f) => f.alias === selectedFeedback,
    );
    let index: number;
    if (currIndex === -1) return;
    if (currIndex === 0) index = 0;
    index = currIndex - 1;
    if (feedbackList?.current[index]?.alias) {
      if (delayTimer.current) {
        clearTimeout(delayTimer.current);
      }
      delayTimer.current = setTimeout(() => {
        setSelectedFeedback(feedbackList?.current[index]?.alias);
        props.onSelectNewModal(
          feedbackList?.current[index]?.alias,
          DETAIL_MODAL_TYPE.FEEDBACK,
        );
        const target = document.querySelector(
          `[data-feedback-id="${feedbackList?.current[index]?.alias}"]`,
        );
        const scrollTarget = document.querySelector(`#${scrollElementId}`);
        if (target && scrollTarget) {
          scrollToTargetAdjusted(target, scrollTarget, delay);
        }
      }, delay);
    }
  };

  const toNextFeedback = () => {
    const currIndex = feedbackList?.current.findIndex(
      (f) => f.alias === selectedFeedback,
    );
    let index: number;
    if (currIndex === -1) return;
    if (currIndex === feedbackList?.current.length - 1)
      index = feedbackList?.current.length - 1;
    index = currIndex + 1;
    if (feedbackList?.current[index]?.alias) {
      if (delayTimer.current) {
        clearTimeout(delayTimer.current);
      }
      delayTimer.current = setTimeout(() => {
        setSelectedFeedback(feedbackList?.current[index]?.alias);
        props.onSelectNewModal(
          feedbackList?.current[index]?.alias,
          DETAIL_MODAL_TYPE.FEEDBACK,
        );
        const target = document.querySelector(
          `[data-feedback-id="${feedbackList?.current[index]?.alias}"]`,
        );
        const scrollTarget = document.querySelector(`#${scrollElementId}`);
        if (target && scrollTarget) {
          scrollToTargetAdjusted(target, scrollTarget, delay);
        }
      }, delay);
    }
  };
  const feedbackList = useMemo(() => {
    const negativeFeedbackList =
      props.data?.feedbacks.filter((feedback) =>
        isFeedbackInCategory(feedback, 'negative'),
      ) || [];

    const positiveFeedbackList =
      props.data?.feedbacks.filter((feedback) =>
        isFeedbackInCategory(feedback, 'positive'),
      ) || [];

    const totalLen = props.data?.feedbacks.length || 0;

    let current = [];

    switch (sentimentCategory) {
      case 'negative':
        current = negativeFeedbackList;
        break;
      case 'positive':
        current = positiveFeedbackList;
        break;
      default:
        current = props.data?.feedbacks || [];
        break;
    }

    const filteredLen = current.length;

    return {
      current: current.slice(0, PAGE_SIZE * page) || [],
      total: totalLen,
      hasNextPage: PAGE_SIZE * page <= filteredLen,
      shares: {
        positive:
          totalLen === 0
            ? 0
            : Math.ceil((positiveFeedbackList.length / totalLen) * 100),
        negative:
          totalLen === 0
            ? 0
            : Math.ceil((negativeFeedbackList.length / totalLen) * 100),
      },
    };
  }, [page, props.data?.feedbacks, sentimentCategory, isFeedbackInCategory]);

  useEffect(() => {
    setToPrevFeedback(toPrevFeedback);
    setToNextFeedback(toNextFeedback);
    return () => {
      setToPrevFeedback(null);
      setToNextFeedback(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackList, setToPrevFeedback, setToNextFeedback, selectedFeedback]);

  let delayTimer = useRef<NodeJS.Timer | null>(null);
  let durationTimer = useRef<NodeJS.Timer | null>(null);

  const onChartSentenceClick = (alias: string) => {
    const target = document.querySelector(`[data-feedback-id="${alias}"]`);

    if (!target) return;
    const scrollTarget = document.querySelector(`#${scrollElementId}`);
    if (!scrollTarget) return;
    if (delayTimer.current) {
      clearTimeout(delayTimer.current);
    }
    if (durationTimer.current) {
      clearTimeout(durationTimer.current);
    }

    const offset = 200;
    const delay = 200;

    delayTimer.current = setTimeout(() => {
      setSelectedFeedback(alias);
      scrollToTargetAdjusted(target, scrollTarget, offset);
    }, delay);

    durationTimer.current = setTimeout(() => {
      setSelectedFeedback(null);
    }, 3000);
  };

  const onIntersect = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      entry.isIntersecting && setPage((prev) => prev + 1);
    });
  };

  useIntersectionObserver({
    target: loaderRef,
    threshold: 0.1,
    onIntersect,
  });

  return (
    <ScrollView
      style={{
        height: 'calc(100vh - 242px)',
        padding: '0px 32px 32px',
      }}
      id={scrollElementId}
    >
      <Style>
        <Sticky
          style={{
            background: color.gray100,
            padding: '16px 0 16px',
            zIndex: 1,
            boxShadow: `0px 6px 8px ${color.gray100}`,
          }}
        >
          <Flex j="space-between" a="flex-end">
            <Radio.Group
              value={sentimentCategory}
              onChange={onChange}
              size="middle"
            >
              <Radio.Button value={'all'}>All</Radio.Button>
              <Radio.Button value={'negative'}>
                Negative ({feedbackList.shares.negative}%)
              </Radio.Button>
              <Radio.Button value={'positive'}>
                Positive ({feedbackList.shares.positive}%)
              </Radio.Button>
            </Radio.Group>
          </Flex>
          <Space h={20} />
          <VFlex g={8}>
            <Flex a="center" g="8px">
              <AIIcon />
              <Typo type="label3" color={color.gray700} weight="b">
                Sentiment trend
              </Typo>
              <GuideInfo
                tooltip={{
                  content: (
                    <SentimentInfo>
                      <SentimentInfo.SentimentColor />
                    </SentimentInfo>
                  ),
                  offset: {
                    top: 30,
                    left: -90,
                  },
                }}
              />
            </Flex>

            <SentimentTrendChart
              onCircleClick={(alias) => {
                onChartSentenceClick(alias);
              }}
              data={parsedChartData}
              mode="uneven"
              labels={chartLabels}
            />
          </VFlex>
        </Sticky>

        <VFlex g={8}>
          {feedbackList.current.map((f) => (
            <SentimentFeedbackItem
              key={f.alias}
              index={f.alias}
              sentiment={f.sentiment}
              text={
                f.key_sentence?.text
                  ? `"${f.key_sentence?.text}"`
                  : f.title || 'Untitled'
              }
              tid={f.tid}
              account_name={f.company_info?.name}
              contact_name={f.customer_info?.name}
              onClick={() => {
                setSelectedFeedback(f.alias);
                props.onSelectNewModal(f.alias, DETAIL_MODAL_TYPE.FEEDBACK);
              }}
              focused={selectedFeedback === f.alias}
            />
          ))}
          {feedbackList.hasNextPage && (
            <Flex ref={loaderRef} style={{ height: 60 }}></Flex>
          )}
          {feedbackList.current.length === 0 && (
            <CenterFlex style={{ height: 300 }}>No feedback</CenterFlex>
          )}
        </VFlex>
        <Space h={50} />
      </Style>
    </ScrollView>
  );
};

const Style = styled(VFlex)`
  gap: 16px;
  padding-bottom: 24px;
`;

export default SentimentTab;
